/**
 * A query string with its values percent-encoded.
 *
 * The encoding is not tidiness. Every value that reached this used to be an id, a
 * timestamp, a number or a flag, so raw interpolation was latent rather than wrong.
 * Server-side device search changed that: a serial containing `&` splits the parameter in
 * two and the server searches for the part before it — wrong rows, under the right total,
 * cached against the full term so the wrong answer comes back on a repeat search. A `#`
 * truncates the rest of the query string outright, silently dropping every parameter
 * after it, which on the device list means the page, the sort and the filters.
 *
 * Keys are encoded for the same reason: they are ours today, but an unencoded key is the
 * same bug waiting. (This mirrors lib/api/queryString.ts, which is the same fix for the
 * other client.)
 *
 * An empty value and an absent one are both DROPPED, which is long-standing behaviour
 * this preserves: a cleared filter must not send `type=`, and on the paged endpoints
 * sending a parameter at all is what opts a request into the paged contract.
 *
 * A `null` value is deliberately NOT dropped, even though it almost certainly means the
 * same thing as `undefined` to every caller. It currently reaches the server as the
 * literal string "null", which matches nothing — so dropping it would turn an empty list
 * into a full one, and that is a visible change to make on purpose with a caller in hand,
 * not as a side effect of an encoding fix.
 */
export const buildQueryParam = (query: object) => {
  return Object.keys(query).reduce((acc: string, curr: string) => {
    const value = query[curr as keyof typeof query];
    const asString = String(value);

    if (asString === "" || asString === "undefined") {
      return acc;
    }

    const pair = `${encodeURIComponent(curr)}=${encodeURIComponent(asString)}`;
    return `${acc}${acc ? "&" : ""}${pair}`;
  }, "");
};
